// Orchestrateur : état de la partie, boucle de jeu, liaison entre moteur, services et interface.
import { DEFAULT_DEV_SETTINGS, STORAGE_KEYS, DOUBLE_TAP_MS, FINAL_LEVEL, INSTALL_OFFER_INTERVAL_DAYS, HINT_BUBBLE_HOLD_MS, MOUSE_RADIUS } from './config/constants.js';
import { gameTheme } from './config/theme.js';
import { generateMaze, findOptimalPath, tryMove, findStraightPath, resolveSwipeStep, slideToward } from './core/maze.js';
import { createGlide, glideTo, advanceGlide } from './core/glide.js';
import { createTrail, extendTrail } from './core/trail.js';
import { getMaxGridSize, getLevelSize, getTimerInterval, getRatInterval } from './core/level.js';
import { scenarioFor } from './core/scenarios/index.js';
import { pathGain, timeLeft } from './core/scenarios/common.js';
import { CHARACTERS, ACTIONS, DEFAULT_CAST } from './data/cast.js';
import { forcedActionFrom, forcedBonusFrom, castParams as castTextParams } from './core/slot.js';
import { sanitizeSummary, insertLocalScore, localId } from './core/leaderboard.js';
import { resolveProfile, isComplete, signatureOf } from './core/profile.js';
import { resolveLanguage, createI18n } from './core/i18n.js';
import { isInstallOfferDue } from './core/install.js';
import { isNativeApp } from './config/native.js';
import { bindSettingsSecret } from './ui/settingsSecret.js';
import { loadLocal, saveLocal } from './services/storage.js';
import { createFirebaseService } from './services/firebase.js';
import { locatePlace } from './services/geolocation.js';
import { createAudioEngine, MAX_VOLUME_STEP } from './services/audio.js';
import { bindHoldRepeat } from './ui/holdRepeat.js';
import { $ } from './ui/dom.js';
import { createRenderer } from './ui/renderer.js';
import { createHintBubbles } from './ui/hintBubbles.js';
import { createOverlay } from './ui/overlay.js';
import { createScoreboard } from './ui/scoreboard.js';
import { createTimeBar } from './ui/timeBar.js';
import { createLeaderboardView } from './ui/leaderboardView.js';
import { createProfileView } from './ui/profileView.js';
import { createDevSettingsView } from './ui/devSettings.js';
import { createLanguageSwitcher } from './ui/language.js';
import { applyTranslations } from './ui/i18n-apply.js';
import { bindInput } from './ui/input.js';
import { createLayout } from './ui/layout.js';
import { createInstallOffer } from './ui/install.js';
import { offerInstallOnce } from './ui/installLaunchPopup.js';
import { createShareModal } from './ui/shareModal.js';
import { createSlotMachine } from './ui/slotMachine.js';
import { dismissSplash } from './ui/splash.js';

export function createGame() {
  // --- État -----------------------------------------------------------------
  const s = {
    grid: [],
    optimalPath: [],
    player: { x: 0, y: 0 },
    playerView: createGlide({ x: 0, y: 0 }), // position affichée de la souris, qui glisse entre les cases (core/glide.js)
    mouseTrail: createTrail({ x: 0.5, y: 0.5 }), // traces de pattes de la souris (core/trail.js)
    cast: DEFAULT_CAST,           // joueur, adversaire et action tirés au bandit manchot (data/cast.js)
    scene: null,                  // état du scénario de l'action tirée (core/scenarios/), null avant le tirage
    currentGain: 0,
    gainMultiplier: 1,            // ×2 à chaque paire tirée au bandit manchot, pour le niveau à venir (core/slot.js#drawSpin)
    currentLevel: 1,
    totalScore: 0,
    state: 'waiting_to_profile', // waiting_to_profile | waiting_to_start | playing | finished
    isPaused: false,
    isTouching: false,             // doigt posé sur le canvas (halo autour de la souris, ui/renderer.js)
    hasMoved: false,              // la souris a bougé depuis le début du niveau
    hintCell: null,               // case désignée par la bulle d'aide (halo, ui/renderer.js)
    lastResult: null,             // 'win' | 'survive' | 'lose'
    devModeUsed: false,           // true dès qu'un réglage dev est appliqué → scores en ligne désactivés
    newGameAfterLeaderboard: false, // score de fin de partie inscrit : refermer le Hall of Fame lance une nouvelle partie
    devSettings: { ...DEFAULT_DEV_SETTINGS },
    profile: null,                 // { pseudo, avatar, place } choisi dans la fenêtre du profil (core/profile.js), null avant
    localTop: sanitizeSummary(loadLocal(STORAGE_KEYS.leaderboard, null)), // Hall of Fame tenu sur l'appareil
    onlineTop: null,               // synthèse du Hall of Fame en ligne, lue à l'ouverture ou rendue par submitScore
    onlineId: loadLocal(STORAGE_KEYS.hallOfFameId, null), // identifiant du joueur dans le Hall of Fame en ligne
    hallOfFameLocal: true,         // le Hall of Fame affiché est celui de l'appareil
    runTicket: null,               // Promise du ticket de la partie en cours (functions/index.js#startRun), null hors ligne
    lastOverlay: null,             // { situation, action, reason } affiché en dernier, pour le redessiner si la langue change
  };
  let ratTimer = null;
  let animationId = null;
  let autoMoveTimer = null;   // avance automatique après un clic souris

  // --- Langue -----------------------------------------------------------
  // Par défaut, la langue du navigateur si elle est supportée (français), sinon l'anglais.
  const browserLanguages = navigator.languages?.length ? navigator.languages : (navigator.language ? [navigator.language] : []);
  const i18n = createI18n(resolveLanguage(loadLocal(STORAGE_KEYS.language, null), browserLanguages));

  // --- Dépendances ----------------------------------------------------------
  const canvas = $('gameCanvas');
  const renderer = createRenderer(canvas, $('canvasContainer'), gameTheme);
  const hints = createHintBubbles($('canvasContainer'), $('playHint'), {
    holdMs: HINT_BUBBLE_HOLD_MS,
    onFocus: (cell) => { s.hintCell = cell; },
  });
  const overlay = createOverlay({ t: i18n.t, getLanguage: i18n.getLanguage });
  // En développement, ?action=<id> impose l'action du bandit manchot pour tester un scénario, ?bonus=pair (triple, none)
  // un tirage bonus (le QR code « Partager » garde le paramètre, pour tester sur téléphone)
  const slot = createSlotMachine({
    t: i18n.t,
    characters: CHARACTERS,
    actions: ACTIONS,
    forcedAction: import.meta.env.DEV ? forcedActionFrom(window.location.search, ACTIONS) : null,
    forcedBonus: import.meta.env.DEV ? forcedBonusFrom(window.location.search) : null,
    onBonus: (bonus) => applyBonus(bonus),
    // Le chenillard du scoreboard s'arrête dès que le bouton Jouer s'affiche (il continue sur un tirage bonus, qu'on relance)
    onReveal: ({ cast }) => { if (cast) scoreboard.setChasing(false); },
    // Sons du bandit : l'audio se débloque dès le clic sur « Nouvelle partie » (geste de l'utilisateur, exigé par iOS).
    // `audio` est créé plus bas ; ces fonctions ne s'exécutent qu'ensuite.
    sound: {
      unlock: () => audio.init(),
      lever: () => audio.playLever(),
      ticks: (times, durationMs) => audio.playReelTicks(times, durationMs),
      stop: (reel) => audio.playReelStop(reel),
      reveal: () => audio.playReveal(),
      start: () => audio.playStart(),
    },
  });
  const scoreboard = createScoreboard({ t: i18n.t });
  const timeBar = createTimeBar();
  const leaderboardView = createLeaderboardView({ t: i18n.t, getLanguage: i18n.getLanguage });
  const devView = createDevSettingsView();
  // Bouton de compte de la rangée du haut : le joueur choisit ici son avatar, son pseudo et partage sa position (pays, ville)
  const profileView = createProfileView({
    t: i18n.t,
    locate: () => locatePlace({ lang: i18n.getLanguage() }),
    onOpen: () => pause(),
    onClose: () => resume(),
    onSave: (profile, { register }) => {
      s.profile = profile;
      saveLocal(STORAGE_KEYS.profile, profile);
      profileView.renderChip(profile);
      if (register) registerScore();
    },
  });
  // Son toujours actif au lancement (musique + volume max) : volume et musique coupés pendant une
  // session ne sont volontairement pas mémorisés pour la suivante. Exception : en développement
  // (serveur Vite), la musique démarre coupée ; les effets sonores restent actifs.
  const audio = createAudioEngine({ bpm: s.devSettings.musicBpm, initialMusicEnabled: !import.meta.env.DEV });
  // Réchauffe Tone.js en arrière-plan dès maintenant : sur iOS, l'audio doit être débloqué de
  // façon synchrone dans le clic sur le bouton musique, un import encore en cours à ce moment-là
  // empêcherait le son de démarrer.
  audio.preload();
  const firebase = createFirebaseService();
  const installOffer = createInstallOffer(); // popup d'installation au lancement (voir start())
  // Popup d'installation espacée : au plus une proposition tous les INSTALL_OFFER_INTERVAL_DAYS
  const installOfferDue = isInstallOfferDue(
    loadLocal(STORAGE_KEYS.installOfferedAt, null), Date.now(), INSTALL_OFFER_INTERVAL_DAYS,
  );

  const draw = () => renderer.draw(s);
  renderer.onAssetsLoaded(draw);
  // Hall of Fame en ligne si Firebase est actif et qu'aucun réglage n'a été modifié ; sinon celui de l'appareil
  const onlineHallOfFame = () => firebase.isActive && !s.devModeUsed;
  const refreshLeaderboard = () => {
    if (!s.hallOfFameLocal) leaderboardView.render(s.onlineTop ?? sanitizeSummary(null), s.onlineId);
    else leaderboardView.render(s.localTop, isComplete(s.profile) ? localId(s.profile) : null);
  };
  const refreshScoreboard = () => scoreboard.update(s);
  const scenario = () => scenarioFor(s.cast.action.id);
  const sceneCtx = () => ({ grid: s.grid, path: s.optimalPath, player: s.player, random: Math.random });
  // Paramètres des textes du scénario : personnages, emoji et phrase de l'action dans les deux sens
  const castParams = () => castTextParams(i18n.t, s.cast);
  // Phrase de fin propre au scénario et à sa cause (« Repéré ! »…), sinon la phrase tirée au bandit
  // dans le sens du résultat (« 🐭 mange 🐱 » ou l'inverse)
  const endPhrase = (situation, reason) => {
    if (situation === 'champion') return i18n.t('slot.result.champion', { levels: FINAL_LEVEL, score: s.totalScore });
    const key = `scenario.${s.cast.action.id}.${reason ?? situation}`;
    const phrase = i18n.t(key, castParams());
    return phrase !== key ? phrase : i18n.t(`slot.result.${situation}`, castParams());
  };
  // Mémorise le dernier écran affiché pour pouvoir le redessiner si la langue change pendant qu'il est visible
  const showOverlay = (situation, action, reason = null) => {
    s.lastOverlay = { situation, action, reason };
    overlay.display(situation, action, endPhrase(situation, reason));
  };

  // --- Classement -----------------------------------------------------------
  // Seule une partie gagnée (le dernier niveau, FINAL_LEVEL) entre au Hall of Fame : le joueur y inscrit son score sous son profil
  // (registerScore). Une partie perdue ou abandonnée n'y laisse rien. En ligne, le score passe par la fonction submitScore, avec
  // le ticket demandé au début de la partie (beginRun) ; le jeu n'écrit jamais dans Firestore.

  /** Une partie commence, à zéro : son ticket, sans lequel son score ne pourra pas entrer au Hall of Fame en ligne. */
  function beginRun() {
    s.runTicket = onlineHallOfFame() ? firebase.startRun() : null;
  }

  /** Ouvre le Hall of Fame, en ligne ou celui de l'appareil ; en ligne, relit la synthèse (un seul document). */
  async function openHallOfFame({ local = !onlineHallOfFame(), fetch = true } = {}) {
    s.hallOfFameLocal = local;
    refreshLeaderboard();
    leaderboardView.open();
    if (local || !fetch) return;
    const top = await firebase.fetchTop();
    if (top) s.onlineTop = top;
    refreshLeaderboard();
  }

  /**
   * Partie gagnée, profil validé : le score est inscrit au Hall of Fame en ligne, ou gardé sur l'appareil (hors ligne, réglages
   * modifiés, sans position, ticket manquant ou score refusé), puis le Hall of Fame s'ouvre, la ligne du joueur en évidence ; le
   * refermer lance une nouvelle partie. Un avatar et un pseudo déjà pris par un autre joueur rouvrent la fenêtre du profil.
   */
  async function registerScore() {
    overlay.close();
    pause();
    const score = s.totalScore;
    const ticket = onlineHallOfFame() && s.profile.place ? await s.runTicket : null;
    if (ticket) {
      const result = await firebase.submitScore({ ticket, score, profile: signatureOf(s.profile) });
      if (result.ok) {
        s.onlineTop = result.top;
        s.onlineId = result.id;
        saveLocal(STORAGE_KEYS.hallOfFameId, result.id);
        s.runTicket = null;
        s.newGameAfterLeaderboard = true;
        openHallOfFame({ local: false, fetch: false });
        return;
      }
      // Le refus annule tout côté serveur : le ticket reste bon pour un autre avatar ou un autre pseudo
      if (result.reason === 'name-taken') {
        profileView.open({ register: true, score, error: 'name-taken' });
        return;
      }
    }
    s.localTop = insertLocalScore(s.localTop, s.profile, score, Date.now());
    saveLocal(STORAGE_KEYS.leaderboard, s.localTop);
    s.newGameAfterLeaderboard = true;
    openHallOfFame({ local: true });
  }

  // --- Cycle de niveau ------------------------------------------------------
  // Labyrinthe prêt, en attente : chaque niveau ne démarre qu'après un tirage au bandit manchot
  // (spinSlot), le premier après validation du profil.
  function initLevel() {
    const gridSize = getLevelSize(s.currentLevel, s.devSettings.sizeStep, getMaxGridSize($('canvasContainer').clientWidth));
    renderer.resize();

    s.grid = generateMaze(gridSize, { twistiness: s.devSettings.twistiness, deadEnds: s.devSettings.deadEnds });
    s.optimalPath = findOptimalPath(s.grid);
    s.player = { x: 0, y: 0 };
    s.playerView = createGlide(s.player);
    s.mouseTrail = createTrail({ x: 0.5, y: 0.5 });
    s.hasMoved = false;
    hints.stop();
    stopAutoMove();
    s.scene = null; // posé au tirage, une fois l'action connue (spinSlot)
    // Dès le bandit manchot, le scoreboard montre le niveau qui vient, les points en jeu et le score : le gain du chemin,
    // remplacé par celui du scénario une fois l'action tirée (spinSlot). Les bonus du tirage précédent ne comptent plus.
    s.gainMultiplier = 1;
    s.currentGain = slotGain();
    refreshScoreboard();
    timeBar.set(1); // plein pour le niveau qui vient
    s.state = 'waiting_to_start';
    draw();
  }

  // Points en jeu avant le tirage (le chemin), puis ceux du scénario ; multipliés par les paires tirées au bandit
  const slotGain = () => pathGain(s.optimalPath) * s.gainMultiplier;
  const levelGain = () => scenario().gain(s.scene) * s.gainMultiplier;

  /**
   * Tirage bonus du bandit manchot (trois personnages, core/slot.js#drawSpin) : une paire double le gain du niveau à venir
   * (×2, ×4… les paires se cumulent), un triple ajoute tout de suite trois fois le gain affiché au score, puis le gain
   * revient à sa valeur de départ. Dans tous les cas, on relance. Rend les paramètres de l'annonce (slot.bonus.<bonus>) ;
   * pour un triple, `until` : la fin de la séquence du score (clignotements, montée), avant laquelle on ne relance pas.
   */
  function applyBonus(bonus) {
    if (bonus === 'pair') s.gainMultiplier *= 2;
    let until = null;
    let points = 0;
    if (bonus === 'triple') {
      points = 3 * s.currentGain;
      const from = s.totalScore;
      s.totalScore += points;
      s.gainMultiplier = 1;
      audio.playWin();
      // Le score ne prend sa valeur qu'au bout de la séquence ; rien au-dessus du gain, le score a déjà monté
      until = scoreboard.celebrateScore(from, s.totalScore, () => audio.playCoin());
    }
    s.currentGain = slotGain();
    refreshScoreboard();
    return { multiplier: s.gainMultiplier, points, until };
  }

  /**
   * Bandit manchot : tire le joueur, l'adversaire et l'action, puis lance le niveau (déjà prêt). Tant qu'il est affiché, la
   * musique est celle du flipper (services/slotMusic.js) ; les encarts du scoreboard s'allument l'un après l'autre jusqu'à ce
   * que le bouton Jouer apparaisse (onReveal).
   */
  function spinSlot() {
    overlay.close();
    scoreboard.setChasing(true);
    audio.setMusicScene('slot');
    slot.open(async (cast) => {
      s.cast = cast;
      s.scene = scenario().create(sceneCtx());
      s.currentGain = levelGain();
      scoreboard.setChasing(false);
      refreshScoreboard();
      await audio.init(); // dans le clic sur Jouer : débloque l'audio sur iOS
      audio.setMusicScene('game');
      startPlaying();
    });
  }

  /** Niveau suivant, rejoué ou relancé : nouveau labyrinthe, nouveau tirage. */
  function newLevel() {
    initLevel();
    spinSlot();
  }

  function startPlaying() {
    s.state = 'playing';
    overlay.close();
    startTimer();
    startAnimation();
  }

  function startTimer() {
    if (ratTimer) clearInterval(ratTimer);
    const interval = getRatInterval(s.currentLevel, s.devSettings.speedFactor);
    ratTimer = setInterval(() => {
      if (s.state !== 'playing' || s.isPaused) return;
      // Joueur immobile : le scénario s'arrête au bout de quelques tics et les bulles expliquent le
      // jeu, jusqu'au premier déplacement (voir enterCell)
      if (!s.hasMoved && scenario().isIdle(s.scene)) {
        if (!hints.isActive()) hints.start(hintSteps);
        return;
      }
      s.scene = scenario().tick(s.scene, sceneCtx());
      applyScene();
    }, interval);
  }

  /**
   * Après un tic ou un déplacement : points en jeu, renvoi éventuel du joueur, fin du niveau.
   * @returns {boolean} false si le joueur a été renvoyé ailleurs
   */
  function applyScene() {
    s.currentGain = levelGain();
    refreshScoreboard();
    // Le trait glisse jusqu'au temps qui reste, sur la durée d'un tic : il arrive quand vient le suivant
    timeBar.set(timeLeft(s.scene), getRatInterval(s.currentLevel, s.devSettings.speedFactor));
    let stayed = true;
    if (s.scene.sendPlayerTo) {
      sendPlayerTo(s.scene.sendPlayerTo);
      s.scene = { ...s.scene, sendPlayerTo: null };
      stayed = false;
    }
    if (s.scene.result) endLevel(s.scene.result);
    return stayed;
  }

  /** Renvoie le joueur sur une case (statues musicales) : sans glissement ni traces jusque-là. */
  function sendPlayerTo(cell) {
    stopAutoMove();
    s.player = { x: cell.x, y: cell.y };
    s.playerView = createGlide(s.player);
    s.mouseTrail = createTrail({ x: cell.x + 0.5, y: cell.y + 0.5 });
  }

  /** Bulles d'aide : le joueur (commandes selon l'appareil), puis celles du scénario. */
  function hintSteps() {
    const gridSize = s.grid.length;
    const isTouch = window.matchMedia?.('(pointer: coarse)').matches ?? false;
    const params = castParams();
    return [
      { text: i18n.t(isTouch ? 'playHint.mouseTouch' : 'playHint.mouseDesktop', params), cell: s.player, gridSize },
      ...scenario().hints(s.scene, sceneCtx()).map(({ key, cell }) => ({ text: i18n.t(key, params), cell, gridSize })),
    ].filter((step) => step.cell);
  }

  function startAnimation() {
    stopAnimation();
    let last = performance.now();
    const loop = (now = performance.now()) => {
      // Écart plafonné : après un onglet en arrière-plan, la souris ne doit pas « téléporter »
      s.playerView = advanceGlide(s.playerView, Math.min(100, now - last));
      // Traces de pattes le long de la position affichée : vrai trajet, au doigt comme au clavier
      s.mouseTrail = extendTrail(s.mouseTrail, { x: s.playerView.x + 0.5, y: s.playerView.y + 0.5 });
      last = now;
      draw();
      animationId = requestAnimationFrame(loop);
    };
    loop();
  }

  function stopAnimation() {
    if (animationId) cancelAnimationFrame(animationId);
    animationId = null;
  }

  // Idempotent : ne fait rien si le niveau n'est plus en cours
  function endLevel(situation) {
    if (s.state !== 'playing') return;
    s.state = 'finished';
    s.lastResult = situation;
    clearInterval(ratTimer);
    hints.stop();
    stopAutoMove();
    stopAnimation();
    s.playerView = createGlide(s.player); // image figée : la souris à sa vraie place
    timeBar.set(timeLeft(s.scene)); // figé sur le temps qui restait (effacé si le temps est écoulé)

    const reason = s.scene?.reason ?? null;
    if (situation === 'lose') {
      audio.playLose();
      showOverlay('lose', 'restart', reason);
    } else {
      s.totalScore += s.currentGain;
      refreshScoreboard();
      if (situation === 'win' && s.currentLevel >= FINAL_LEVEL) {
        // Dernier niveau gagné : la partie est gagnée, le joueur peut inscrire son score au Hall of Fame
        audio.playWin();
        showOverlay('champion', 'register');
      } else if (situation === 'win') {
        audio.playWin();
        showOverlay('win', 'next', reason);
      } else {
        audio.playSurvive();
        showOverlay('survive', 'restart', reason);
      }
    }
    draw(); // dernière image figée
  }

  /**
   * Le joueur entre dans une case (premier déplacement : l'adversaire repart ; le scénario décide
   * du reste : objet ramassé, adversaire atteint, renvoi au départ…).
   * @returns {boolean} false si le joueur a été renvoyé ailleurs ou si le niveau est fini
   */
  function enterCell(cell) {
    if (s.state !== 'playing') return false;
    s.player = cell;
    if (!s.hasMoved) {
      s.hasMoved = true;
      hints.stop(); // l'adversaire repart au prochain tic
    }
    s.scene = scenario().enter(s.scene, sceneCtx());
    return applyScene() && s.state === 'playing';
  }

  /** Déplace le joueur d'une case si aucun mur ne bloque. Retourne true si déplacé. */
  function step(direction) {
    const next = tryMove(s.grid, s.player, direction);
    if (!next) return false;
    s.playerView = glideTo(s.playerView, next);
    return enterCell(next);
  }

  /** Déplacement manuel au clavier : interrompt toute avance automatique en cours. */
  function move(direction) {
    stopAutoMove();
    return step(direction);
  }

  /**
   * Souris traînée au doigt : mouvement libre vers le point visé, sans traverser les murs
   * (core/maze.js#slideToward). L'affichage suit exactement ; chaque case traversée est
   * enregistrée dans l'ordre, pour détecter le fromage au passage.
   */
  function dragToward(point) {
    stopAutoMove();
    const from = { x: s.playerView.x + 0.5, y: s.playerView.y + 0.5 };
    const { pos, cells } = slideToward(s.grid, from, point, MOUSE_RADIUS);
    s.playerView = createGlide({ x: pos.x - 0.5, y: pos.y - 0.5 });
    for (const cell of cells) {
      if (!enterCell(cell)) break;
    }
  }

  /** Fin du geste : la souris glisse jusqu'au centre de la case où elle se trouve. */
  function dragEnd() {
    if (s.state !== 'playing') return;
    s.playerView = glideTo(createGlide(s.playerView), s.player);
  }

  /** Pas de glissement à deux doigts (trackpad) : direction dominante, perpendiculaire ou virage du couloir (core/maze.js). */
  function swipeStep({ primary, secondary }) {
    stopAutoMove();
    const direction = resolveSwipeStep(s.grid, s.player, primary, secondary);
    if (!direction) return null;
    step(direction);
    return direction;
  }

  function stopAutoMove() {
    if (autoMoveTimer) clearInterval(autoMoveTimer);
    autoMoveTimer = null;
  }

  /**
   * Clic souris : la souris avance vers la case visée si elle est alignée et sans mur,
   * un pas tout de suite puis un pas par intervalle du chrono (le rat, lui, est ralenti
   * sur les premiers niveaux).
   */
  function moveTo(target) {
    const path = findStraightPath(s.grid, s.player, target);
    if (!path) return false;
    stopAutoMove();

    let remaining = path.steps;
    const advance = () => {
      if (s.state !== 'playing') { stopAutoMove(); return; }
      if (s.isPaused) return;
      if (!step(path.direction)) { stopAutoMove(); return; }
      remaining--;
      if (remaining <= 0 || s.state !== 'playing') stopAutoMove();
    };

    advance();
    if (remaining > 0) {
      autoMoveTimer = setInterval(advance, getTimerInterval(s.currentLevel, s.devSettings.speedFactor));
    }
    return true;
  }

  // --- Pause (modals) -------------------------------------------------------
  function pause() {
    s.isPaused = true;
    audio.pause();
  }
  function resume() {
    s.isPaused = false;
    audio.resume();
  }

  // --- Liaison interface ----------------------------------------------------
  function bindUI() {
    bindInput(canvas, {
      onMove: move,
      onDragToward: dragToward,
      onDragEnd: dragEnd,
      getGrabPoint: () => ({ x: s.playerView.x + 0.5, y: s.playerView.y + 0.5 }),
      onSwipeStep: swipeStep,
      onClickCell: moveTo,
      onPressChange: (pressed) => { s.isTouching = pressed; },
      isActive: () => s.state === 'playing' && !s.isPaused,
      getSwipeThreshold: () => s.devSettings.playerSpeed,
      getGridSize: () => s.grid.length,
    });

    // Taille du labyrinthe ajustée à l'espace que laissent le nom, le scoreboard et les boutons (et au redimensionnement)
    createLayout({
      area: $('gameArea'),
      blocks: [$('gameHeader'), $('gameScore'), $('gameFooter')],
      maze: $('canvasContainer'),
      onChange: () => {
        if (s.grid.length === 0) return;
        if (renderer.resize()) draw();
        hints.refresh();
      },
    });

    // Boutons de l'overlay central
    overlay.buttons.next.addEventListener('click', () => {
      s.currentLevel++;
      newLevel();
    });
    // Partie gagnée : la fenêtre du profil s'ouvre pour inscrire le score sous son pseudo (onSave, puis registerScore)
    overlay.buttons.register.addEventListener('click', () => {
      profileView.open({ register: true, score: s.totalScore });
    });
    overlay.buttons.restart.addEventListener('click', () => {
      // Après une défaite (action faite par l'adversaire), la progression repart de zéro
      if (s.lastResult === 'lose') {
        s.totalScore = 0;
        s.currentLevel = s.devSettings.startLevel;
        beginRun();
      }
      newLevel();
    });

    // Musique de fond on/off : indépendante du volume et des effets (voir services/audio.js).
    const btnMusicToggle = $('btnMusicToggle');
    const refreshMusicButton = () => {
      const on = audio.getMusicEnabled();
      btnMusicToggle.setAttribute('aria-pressed', String(on));
      btnMusicToggle.classList.toggle('is-off', !on); // grisé quand la musique est coupée (.btn-chip)
    };
    btnMusicToggle.addEventListener('click', () => {
      const enabled = !audio.getMusicEnabled();
      audio.setMusicEnabled(enabled);
      refreshMusicButton();
    });
    refreshMusicButton();

    // Volume, comme ZERO KAMI : « Son - » et « Son + », un cran par appui ; un appui long répète (voir ui/holdRepeat.js)
    // et s'arrête au bout de la course, où le bouton est désactivé. S'applique à la musique (si activée) et aux effets.
    const btnVolumeDown = $('btnVolumeDown');
    const btnVolumeUp = $('btnVolumeUp');
    const refreshVolumeButtons = () => {
      const step = audio.getVolumeStep();
      btnVolumeDown.disabled = step <= 0;
      btnVolumeUp.disabled = step >= MAX_VOLUME_STEP;
    };
    // Remonter le volume alors que la musique est coupée (bouton musique off) doit la réactiver :
    // sinon le cran de volume change sans effet audible et l'utilisateur croit le réglage cassé.
    const reactivateMusicIfNeeded = () => {
      if (audio.getMusicEnabled()) return;
      audio.setMusicEnabled(true);
      refreshMusicButton();
    };
    // Retourne true au bout de la course (coupé ou au maximum), pour que l'appui long s'arrête net.
    const stepVolume = (delta) => () => {
      const next = Math.max(0, Math.min(MAX_VOLUME_STEP, audio.getVolumeStep() + delta));
      audio.setVolumeStep(next);
      if (delta > 0) reactivateMusicIfNeeded();
      refreshVolumeButtons();
      return next === 0 || next === MAX_VOLUME_STEP;
    };
    bindHoldRepeat(btnVolumeDown, stepVolume(-1));
    bindHoldRepeat(btnVolumeUp, stepVolume(1));
    refreshVolumeButtons();

    // Hall of Fame — accès direct : l'installation ne se propose plus qu'une fois, au lancement
    // (voir installLaunchPopup.js dans start()).
    leaderboardView.btnOpen.addEventListener('click', () => {
      pause();
      openHallOfFame();
    });
    leaderboardView.btnClose.addEventListener('click', () => {
      leaderboardView.close();
      resume();
      // Score de fin de partie inscrit : on referme le Hall of Fame sur une nouvelle partie, du premier niveau, à zéro
      if (s.newGameAfterLeaderboard) {
        s.newGameAfterLeaderboard = false;
        s.totalScore = 0;
        s.currentLevel = s.devSettings.startLevel;
        beginRun();
        newLevel();
      }
    });

    // Partager : QR code de l'URL courante pour ouvrir le jeu sur un autre appareil
    const shareModal = createShareModal();
    shareModal.btnOpen.addEventListener('click', () => {
      pause();
      shareModal.open();
    });
    shareModal.btnClose.addEventListener('click', () => {
      shareModal.close();
      resume();
    });

    // Réglages cachés, comme ZERO KAMI : double appui (ou double clic) sur « 0cog™ » dans les mentions légales du pied, sans rien
    // d'apparent (ui/settingsSecret.js), sur téléphone comme sur ordinateur. Pas dans une appli native des boutiques (règle 2.3.1 de
    // l'App Store, config/native.js). Rien ne s'ouvre par-dessus une autre fenêtre ou l'écran de fin de niveau.
    if (!isNativeApp()) bindSettingsSecret({
      canOpen: () => !document.querySelector('.dialog-overlay:not([hidden])'),
      onOpen: () => {
        pause();
        devView.open(s.devSettings);
      },
    });
    devView.btnClose.addEventListener('click', () => {
      devView.close();
      resume();
    });
    devView.btnSave.addEventListener('click', () => {
      s.devSettings = devView.read();
      s.devModeUsed = true;
      audio.setBpm(s.devSettings.musicBpm);
      if (s.currentLevel < s.devSettings.startLevel) s.currentLevel = s.devSettings.startLevel;
      devView.close();
      s.isPaused = false;
      newLevel(); // applique immédiatement taille et vitesse
    });

    // Langue : réglage utilisateur indépendant des curseurs de gameplay ci-dessus, bascule immédiate
    // (ne marque jamais devModeUsed — changer de langue n'affecte ni le score ni la difficulté)
    const languageSwitcher = createLanguageSwitcher({
      onSelect: (lang) => {
        i18n.setLanguage(lang);
        saveLocal(STORAGE_KEYS.language, lang);
        applyTranslations(i18n.getDict());
        languageSwitcher.reflect(lang);
        hints.refresh();
        refreshLeaderboard();
        // Si l'écran de fin de niveau (ou l'accueil) est visible, le redessiner dans la nouvelle langue
        if (s.lastOverlay && !$('gameOverlay').hidden) {
          showOverlay(s.lastOverlay.situation, s.lastOverlay.action, s.lastOverlay.reason);
        }
        slot.refresh();
        refreshScoreboard(); // annonce au-dessus du gain
        profileView.renderChip(s.profile);
        profileView.refresh();
      },
    });
    languageSwitcher.reflect(i18n.getLanguage());
  }

  // --- Démarrage ------------------------------------------------------------
  async function start() {
    applyTranslations(i18n.getDict()); // avant tout affichage, pour éviter un flash dans la mauvaise langue
    bindUI();

    // Installation : seule occasion offerte, une fois au lancement (jamais si déjà installé, ni
    // si déjà proposée il y a moins de INSTALL_OFFER_INTERVAL_DAYS) ; ensuite, plus aucun moyen
    // d'installer depuis le jeu sans recharger la page.
    offerInstallOnce(installOffer, {
      due: installOfferDue,
      onPresented: () => saveLocal(STORAGE_KEYS.installOfferedAt, Date.now()),
    });

    // Pas d'écran de profil au départ : le profil mémorisé s'il y en a un ; sinon le joueur le crée en inscrivant son premier
    // score au Hall of Fame (ou par le bouton de compte)
    s.profile = resolveProfile(loadLocal(STORAGE_KEYS.profile, null));
    profileView.renderChip(s.profile);

    initLevel();

    // L'écran d'accueil s'efface en fondu une fois le délai minimal passé et les polices prêtes ;
    // ne dépend pas de Firebase, dont la connexion peut prendre plus de temps ou échouer. Le
    // premier tirage au bandit manchot suit aussitôt.
    await dismissSplash({ ready: document.fonts?.ready ?? Promise.resolve() });
    spinSlot();

    // Firebase en arrière-plan : le jeu est jouable hors ligne pendant ce temps
    // Le premier tirage a déjà commencé : le ticket de cette première partie part dès que Firebase est prêt
    if (await firebase.init()) beginRun();
  }

  return { start, get state() { return s; } };
}
